'use client'

import {
  ComponentProps,
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  type ColumnDef,
  type ExpandedState,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  ColumnSort,
  Row,
} from '@tanstack/react-table'
import { cva, cx, type VariantProps } from 'class-variance-authority'
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft'
import { ArrowRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowRight'
import { FunnelXIcon } from '@phosphor-icons/react/dist/ssr/FunnelX'
import { tableDataToCsv, csvDownload } from '@skop-app/lib-domain/utils'

import { Button } from '@/components/atoms/Button/button.atom'
import { Checkbox } from '@/components/atoms/Checkbox/checkbox.atom'
import { Input } from '@/components/atoms/Input/input.atom'
import { Select } from '@/components/atoms/Select/select.atom'
import { Table } from '@/components/atoms/Table/table.atom'
import { ColumnsManager } from './columnsManager.molecule'

const PaginationButtons = ({
  table,
}: {
  table: any
  variant?: 'default' | 'light'
}) => (
  <div className='flex items-center gap-2'>
    <Button
      variant='outline'
      size='sm'
      onClick={() => table.previousPage()}
      disabled={!table.getCanPreviousPage()}
    >
      <ArrowLeftIcon className='size-4' />
    </Button>
    <Button
      variant='outline'
      size='sm'
      onClick={() => table.nextPage()}
      disabled={!table.getCanNextPage()}
    >
      <ArrowRightIcon className='size-4' />
    </Button>
  </div>
)

type Label = {
  label: string
  value: string
}

type Prefilter = {
  key: string
  default?: string | Label
  placeholder?: string
}

type DataTableProps = {
  columns: ColumnDef<any>[]
  data: any
  isLoaded: boolean
  pageSize?: number
  search?: string | string[]
  defaultSorting?: ColumnSort[]
  actionBtn?: ReactNode
  selectBtn?: ReactNode
  columnsBtnTitle?: string
  columnsBtnIcon?: ReactNode
  emptyMessage?: string
  loadingMessage?: string
  searchMessage?: string
  variant?: 'default' | 'light'
  enableRowSelection?: boolean
  onRowSelectionChange?: (selection: RowSelectionState) => void
  selectionText?: string
  quickActions?: Label[]
  onQuickActionSelect?: (action: Label, selectedRows: any[]) => void
  pagination?: boolean
  getRowStyles?: (row: Row<any>) => CSSProperties
  className?: string
  searchClassName?: string
  prefilter?: Prefilter
  clearFiltersLabel?: string
  onFilteredRowsChange?: (rows: any[]) => void
}

export const DataTable = (props: DataTableProps) => {
  const {
    columns,
    data,
    isLoaded,
    pageSize = 15,
    search,
    defaultSorting = [],
    actionBtn,
    selectBtn,
    emptyMessage = 'Aucune donnée',
    loadingMessage = 'En cours de chargement...',
    searchMessage,
    variant = 'default',
    enableRowSelection = false,
    onRowSelectionChange,
    selectionText = 'sélectionnée(s)',
    quickActions = [],
    onQuickActionSelect,
    pagination = true,
    getRowStyles,
    className,
    searchClassName,
    prefilter,
    clearFiltersLabel,
    onFilteredRowsChange,
  } = props

  const [sorting, setSorting] = useState(defaultSorting)
  const [columnFilters, setColumnFilters] = useState<any[]>(() => {
    if (prefilter?.default) {
      const value =
        typeof prefilter.default === 'object'
          ? prefilter.default.value
          : prefilter.default
      return [{ id: prefilter.key, value }]
    }
    return []
  })
  const [globalFilter, setGlobalFilter] = useState('')
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({})

  const selectionColumn: ColumnDef<any> = {
    id: 'select',
    size: 40,
    header: ({ table }) => (
      <Checkbox
        name='select-all'
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        aria-label='Select all'
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        name={`select-row-${row.id}`}
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
        aria-label='Select row'
      />
    ),
  }

  const finalColumns = useMemo(
    () => (enableRowSelection ? [selectionColumn, ...columns] : columns),
    [columns, enableRowSelection],
  )

  const globalFilterFn = (row: any, _columnId: string, value: string) => {
    if (!Array.isArray(search) || !value) return true

    const searchValue = value.toLowerCase()
    return search.some((columnKey) => {
      const cellValue = row.getValue(columnKey)
      if (cellValue == null) return false
      return String(cellValue).toLowerCase().includes(searchValue)
    })
  }

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.teams,
    enableRowSelection,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater
      setRowSelection(newSelection)
      onRowSelectionChange?.(newSelection)
    },
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: Array.isArray(search) ? globalFilterFn : undefined,
    initialState: {
      pagination: {
        pageSize,
      },
    },
    state: {
      sorting,
      columnFilters,
      globalFilter,
      expanded,
      rowSelection,
      columnOrder,
      columnVisibility,
    },
  })

  const hasActiveSorting =
    sorting.length !== defaultSorting.length ||
    sorting.some(
      (s, i) =>
        s.id !== defaultSorting[i]?.id || s.desc !== defaultSorting[i]?.desc,
    )

  const hasActiveFilters = columnFilters.length > 0 || hasActiveSorting

  const clearAllFilters = () => {
    setColumnFilters([])
    table.resetColumnFilters()
    setSorting(defaultSorting)
  }

  const filteredRows = table.getFilteredRowModel().rows
  useEffect(() => {
    onFilteredRowsChange?.(filteredRows.map((row) => row.original))
  }, [filteredRows, onFilteredRowsChange])

  const prefilterOptions = useMemo(() => {
    if (!prefilter?.key) return []

    const colId = prefilter.key
    const rows = table.getCoreRowModel().rows
    const uniqueValues = new Map()

    rows.forEach((row) => {
      const value = row.getValue(colId)
      if (value !== undefined && value !== null) {
        uniqueValues.set(String(value), value)
      }
    })

    return Array.from(uniqueValues.entries())
      .map(([label, value]) => ({
        label: String(label),
        value: String(value),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [table.getCoreRowModel().rows, prefilter?.key])

  const showBottomPagination = !enableRowSelection || variant === 'light'

  const footerControlsVariants = cva('flex items-center gap-4 w-full', {
    variants: {
      variant: {
        default: '',
        light:
          'border-t font-medium p-4 text-sm text-gray-900 whitespace-nowrap',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  })

  type FooterControlsProps = ComponentProps<'div'> &
    VariantProps<typeof footerControlsVariants>

  const FooterControls = (props: FooterControlsProps) => {
    const { variant, className, ...rest } = props

    const downloadCsv = () => {
      const headerGroup = table.getHeaderGroups()[0]
      const csvColumns = headerGroup.headers
        .filter((header) => header.column.id !== 'select')
        .map((header) => {
          const col = header.column.columnDef as ColumnDef<any> & {
            accessorKey?: string
            meta?: {
              csvLabel?: string
              [key: string]: any
            }
          }

          let headerLabel = ''
          if (col.meta?.csvLabel) {
            headerLabel = col.meta.csvLabel
          } else if (typeof col.header === 'string') {
            headerLabel = col.header
          } else {
            headerLabel = header.column.id ?? col.accessorKey ?? ''
          }

          return {
            accessorKey: col.accessorKey,
            id: header.column.id,
            header: headerLabel,
          }
        })

      const rowValues = table.getSelectedRowModel().rows.map((row) => {
        const rowData = {}
        csvColumns.forEach((col) => {
          const key = col.accessorKey || col.id
          const tableCol = table.getColumn(col.id || col.accessorKey!)
          const def = tableCol?.columnDef as ColumnDef<any> & {
            meta?: { csvValue?: (r: any) => any }
          }
          const value = def?.meta?.csvValue
            ? def.meta.csvValue(row.original)
            : row.renderValue(key)
          rowData[key] = value
        })
        return rowData
      })
      const csv = tableDataToCsv(rowValues, csvColumns)
      csvDownload(csv, `export-${new Date().toISOString().split('T')[0]}.csv`)
    }

    return (
      <div
        className={cx(footerControlsVariants({ variant }), className)}
        {...rest}
      >
        {enableRowSelection && (
          <div className='flex items-center gap-4'>
            <div className='text-slate-100 font-light'>
              {table.getSelectedRowModel().rows.length}/
              {table.getRowModel().rows.length} {selectionText}
            </div>
            {((enableRowSelection &&
              table.getSelectedRowModel().rows.length > 0) ||
              quickActions.length > 0) && (
              <Select
                isDisabled={table.getSelectedRowModel().rows.length === 0}
                onChange={(selected) => {
                  if (selected && onQuickActionSelect) {
                    onQuickActionSelect(
                      selected as Label,
                      table
                        .getSelectedRowModel()
                        .rows.map((row) => row.original),
                    )
                  }
                }}
                options={[
                  ...quickActions,
                  ...(table.getSelectedRowModel().rows.length > 0
                    ? [
                        {
                          label: 'Exporter CSV',
                          value: 'export-csv',
                          type: 'custom',
                          onClick: downloadCsv,
                        },
                      ]
                    : []),
                ]}
                placeholder='Actions rapides'
                value={null}
                className='w-[200px]'
                overflow
              />
            )}
          </div>
        )}
        {showBottomPagination && (
          <div className='ml-auto'>
            <PaginationButtons table={table} />
          </div>
        )}
      </div>
    )
  }

  // Fixed and active columns handling
  const allLeafColumns = table.getAllLeafColumns()
  const fixedColumnIds = useMemo(
    () =>
      allLeafColumns
        .filter((c) => (c.columnDef as any)?.fixed)
        .map((c) => c.id),
    [allLeafColumns],
  )

  useEffect(() => {
    if (allLeafColumns.length === 0) return

    const currentIds = allLeafColumns.map((c) => c.id)

    // Initialize or reconcile column order with current columns
    setColumnOrder((prev) => {
      if (prev.length === 0) {
        table.setColumnOrder(currentIds)
        return currentIds
      }
      const kept = prev.filter((id) => currentIds.includes(id))
      const added = currentIds.filter((id) => !kept.includes(id))
      const next = [...kept, ...added]
      const changed =
        next.length !== prev.length || next.some((id, i) => id !== prev[i])
      if (changed) {
        table.setColumnOrder(next)
        return next
      }
      return prev
    })

    // Initialize or reconcile column visibility with current columns
    setColumnVisibility((prev) => {
      const next: Record<string, boolean> = {}
      for (const id of currentIds) {
        const col = allLeafColumns.find((c) => c.id === id)!
        const defaultVisible = col.getIsVisible?.() ?? true
        next[id] = prev[id] ?? defaultVisible
      }
      // Enforce visibility for fixed and select columns
      fixedColumnIds.forEach((id) => {
        next[id] = true
      })
      if ('select' in next) next['select'] = true

      const prevKeys = Object.keys(prev)
      const nextKeys = Object.keys(next)
      const visibilityChanged =
        prevKeys.length !== nextKeys.length ||
        nextKeys.some((k) => prev[k] !== next[k])
      if (visibilityChanged) return next
      return prev
    })
  }, [allLeafColumns, fixedColumnIds])

  const toggleColumnVisibility = (id: string, next: boolean) => {
    if (id === 'select' || fixedColumnIds.includes(id)) return
    table.getColumn(id)?.toggleVisibility(next)
    setColumnVisibility((prev) => ({ ...prev, [id]: next }))
  }

  return (
    <div className={cx('flex flex-col', className)}>
      <div className='mb-4 flex justify-between gap-2 sm:gap-4'>
        <div className='flex gap-2 sm:gap-4'>
          {selectBtn && selectBtn}

          {search && (
            <Input
              type='search'
              placeholder={searchMessage ?? 'Rechercher...'}
              value={
                Array.isArray(search)
                  ? (table.getState().globalFilter ?? '')
                  : (table.getColumn(search)?.getFilterValue() ?? ('' as any))
              }
              onChange={(event) => {
                if (Array.isArray(search)) {
                  table.setGlobalFilter(event.target.value)
                } else {
                  table.getColumn(search)?.setFilterValue(event.target.value)
                }
              }}
              className={cx('max-w-xs', searchClassName)}
            />
          )}
          {prefilter && (
            <Select
              options={prefilterOptions}
              value={
                prefilterOptions.find(
                  (opt) =>
                    opt.value ===
                    (table
                      .getColumn(prefilter.key)
                      ?.getFilterValue() as string),
                ) || null
              }
              onChange={(selected) => {
                const val = (selected as Label)?.value
                table.getColumn(prefilter.key)?.setFilterValue(val)
              }}
              placeholder={prefilter.placeholder ?? 'Filtrer...'}
              className='min-w-[200px]'
              isClearable
            />
          )}
        </div>
        <div className='flex items-center gap-2'>
          {clearFiltersLabel && hasActiveFilters && (
            <Button
              variant='outline'
              size='sm'
              onClick={clearAllFilters}
              className='flex items-center gap-1.5 text-slate-500'
            >
              <FunnelXIcon className='size-4' />
              {clearFiltersLabel}
            </Button>
          )}
          <ColumnsManager
            table={table}
            columnOrder={columnOrder}
            onColumnOrderChange={(next) => {
              setColumnOrder(next)
              table.setColumnOrder(next)
            }}
            fixedColumnIds={fixedColumnIds}
            columnVisibility={columnVisibility}
            onToggleColumnVisibility={toggleColumnVisibility}
            columnsBtnTitle={props.columnsBtnTitle}
            columnsBtnIcon={props.columnsBtnIcon}
          />
          {actionBtn && actionBtn}
        </div>
      </div>
      <Table.Root variant={variant}>
        <Table.Header variant={variant}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id} variant={variant}>
              {headerGroup.headers.map((header) => {
                return (
                  <Table.Head
                    key={header.id}
                    variant={variant}
                    style={{ width: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </Table.Head>
                )
              })}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body variant={variant}>
          {table?.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <Table.Row
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                variant={variant}
                style={getRowStyles?.(row)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    variant={variant}
                    style={{ width: `${cell.column.getSize()}px` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))
          ) : (
            <Table.Row variant={variant}>
              <Table.Cell
                colSpan={
                  table.getHeaderGroups()[0]?.headers.length ??
                  Math.max(finalColumns.length, 1)
                }
                className='w-full text-center'
                variant={variant}
              >
                {isLoaded ? emptyMessage : loadingMessage}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
        {enableRowSelection && variant !== 'light' && (
          <Table.Footer variant={variant}>
            <Table.Row
              variant={variant}
              className='border-t font-medium [&>tr]:last:border-b-0'
            >
              <Table.Cell
                colSpan={finalColumns.length}
                className='p-4 text-sm text-gray-900 whitespace-nowrap'
                variant={variant}
              >
                <FooterControls variant={variant} />
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        )}
      </Table.Root>
      {variant === 'light' && <FooterControls variant={variant} />}
      {pagination && variant !== 'light' && (
        <div className='flex items-center justify-end space-x-2 py-4'>
          <PaginationButtons table={table} variant={variant} />
        </div>
      )}
    </div>
  )
}
